/**
 * The one way the three apps talk to the API. Every route, body and response
 * is typed from the API's own source, so a renamed field is a compile error in
 * the app, not a blank cell in production.
 *
 * Apps call the API on their own origin (`/api/...`), which their web server
 * forwards to the API service (ADR-0003). There is no base URL to configure.
 */

import type { AppType } from "@lumberset/api";
import { adminClient, organizationClient } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";
import { hc, type InferResponseType } from "hono/client";

/** A failed API call, carrying the API's machine-readable code and human message. */
export class ApiClientError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

export const api = hc<AppType>("/", { init: { credentials: "include" } }).api.v1;

/**
 * Awaits a call and returns its JSON, or throws an `ApiClientError`.
 *
 *   const { projects } = await unwrap(api.projects.$get({ query: {} }));
 */
export async function unwrap<
  Response extends { ok: boolean; status: number; json(): Promise<unknown> },
>(call: Promise<Response>): Promise<SuccessBody<Response>> {
  let response: Response;
  try {
    response = await call;
  } catch {
    throw new ApiClientError(0, "network", "Could not reach LumberSet. Check your connection.");
  }

  const payload = (await response.json().catch(() => null)) as {
    error?: { code?: string; message?: string };
  } | null;

  if (!response.ok) {
    throw new ApiClientError(
      response.status,
      payload?.error?.code ?? "unknown",
      payload?.error?.message ?? "Something went wrong.",
    );
  }
  return payload as SuccessBody<Response>;
}

type SuccessBody<Response> = Response extends { json(): Promise<infer Body> }
  ? Exclude<Body, { error: unknown }>
  : never;

export const auth = createAuthClient({
  basePath: "/api/auth",
  plugins: [organizationClient(), adminClient()],
});

export type { InferResponseType };
